/* Der Zeitraum, den der Kunde waehlt.
 *
 * Bis zum 16.09. gab es drei feste Spannen (7/30/90 Tage), die immer am
 * neuesten Geschaeftstag endeten - und kein Feld, um sie zu verschieben:
 * Wer den Vormonat sehen wollte, musste erst in die Tagesansicht, dort
 * ein Datum waehlen und dann zurueckwechseln. Das hat niemand gefunden.
 *
 * Jetzt: zwei Datumsfelder und Knoepfe fuer die Faelle, in denen ein
 * Gastronom wirklich denkt. **Kalendermonate, nicht 30 Tage** - die
 * Abrechnung laeuft nach Monaten, und "die letzten 30 Tage" ist eine
 * Groesse, die es auf keinem Beleg gibt.
 *
 * Alles hier ist rein gerechnet und nimmt `heute` als Angabe entgegen,
 * statt die Uhr zu lesen. Nur so laesst es sich pruefen - und geprueft
 * wird es, gegen Monatsanfaenge, Jahresgrenzen, Schaltjahr und
 * Sommerzeit (`frontend/pruefung/zeitraum.mjs`).
 */

import { alsIso } from './format.ts'

export interface Zeitraum { von: string; bis: string }

/** Immer mit Uhrzeit, nie als blosses Datum.
 *
 * `new Date('2026-09-16')` wird als **UTC** ausgelegt; `getDate()` liest
 * danach die lokale Zeit. In jeder Zeitzone hinter UTC ist das der Tag
 * davor. Gemessen unter America/New_York: aus „gestern" am 16.09. wird
 * der **14.09.** - ein Tag zu weit, und zwar still.
 *
 * Mit Uhrzeit ist es eine lokale Zeit, und dann stimmt es. Mittag statt
 * Mitternacht ist dabei nur der Gurt: Beides rechnet richtig (gemessen
 * in Berlin, New York und Auckland, ueber beide Uhrumstellungen), aber
 * Mittag haelt auch dann noch Abstand, wenn jemand spaeter Stunden
 * addiert.
 */
const tag = (iso: string): Date => new Date(iso + 'T12:00:00')

const verschoben = (iso: string, tage: number): string => {
  const d = tag(iso)
  d.setDate(d.getDate() + tage)
  return alsIso(d)
}

/** Montag der Woche, in der `iso` liegt. Deutsche Woche, nicht amerikanische. */
const montag = (iso: string): string => {
  const d = tag(iso)
  const wochentag = (d.getDay() + 6) % 7        // Mo = 0 … So = 6
  d.setDate(d.getDate() - wochentag)
  return alsIso(d)
}

const monatsErster = (iso: string): string => iso.slice(0, 7) + '-01'

const monatsLetzter = (iso: string): string => {
  const d = tag(iso)
  d.setMonth(d.getMonth() + 1, 0)              // Tag 0 = letzter des Vormonats
  return alsIso(d)
}

const monateFrueher = (iso: string, n: number): string => {
  const d = tag(monatsErster(iso))
  d.setMonth(d.getMonth() - n)
  return alsIso(d)
}

export interface Vorgabe {
  schluessel: string
  name: string
  /** Der laufende Zeitraum endet **heute**, der abgeschlossene an seinem
   *  letzten Tag. Ein Zeitraum in der Zukunft hat keine Daten. */
  rechnen: (heute: string) => Zeitraum
}

export const VORGABEN: Vorgabe[] = [
  { schluessel: 'gestern', name: 'Gestern',
    rechnen: (h) => ({ von: verschoben(h, -1), bis: verschoben(h, -1) }) },
  { schluessel: 'woche', name: 'Diese Woche',
    rechnen: (h) => ({ von: montag(h), bis: h }) },
  { schluessel: 'vorwoche', name: 'Letzte Woche',
    rechnen: (h) => ({ von: montag(verschoben(h, -7)),
                       bis: verschoben(montag(h), -1) }) },
  { schluessel: 'monat', name: 'Dieser Monat',
    rechnen: (h) => ({ von: monatsErster(h), bis: h }) },
  { schluessel: 'vormonat', name: 'Vormonat',
    rechnen: (h) => ({ von: monateFrueher(h, 1),
                       bis: monatsLetzter(monateFrueher(h, 1)) }) },
  { schluessel: 'jahr', name: 'Dieses Jahr',
    rechnen: (h) => ({ von: h.slice(0, 4) + '-01-01', bis: h }) },
  { schluessel: 'vorjahr', name: 'Vorjahr',
    rechnen: (h) => {
      const j = String(Number(h.slice(0, 4)) - 1)
      return { von: `${j}-01-01`, bis: `${j}-12-31` }
    } },
]

/** Welcher Knopf sieht gedrueckt aus? Keiner, wenn von Hand getippt wurde. */
export const passendeVorgabe = (
  von: string, bis: string, heute: string,
): string | null => {
  for (const v of VORGABEN) {
    const z = v.rechnen(heute)
    if (z.von === von && z.bis === bis) return v.schluessel
  }
  return null
}

export const tageImZeitraum = (von: string, bis: string): number => {
  if (!von || !bis || von > bis) return 0
  const ms = tag(bis).getTime() - tag(von).getTime()
  return Math.round(ms / 86400000) + 1
}

const MONAT = new Intl.DateTimeFormat('de-DE', { month: 'long' })
const TAG_VOLL = new Intl.DateTimeFormat('de-DE',
  { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

/** „1. – 30. September 2026 · 30 Tage" - ausgeschrieben.
 *
 * Wer einen Bildschirm abfotografiert oder eine Zahl weitergibt, hat den
 * Zeitraum dabei. Sonst steht da eine Zahl ohne Bezug, und die naechste
 * Frage ist „von wann bis wann eigentlich?".
 */
export const zeitraumText = (von: string, bis: string): string => {
  if (!von || !bis) return ''
  if (von > bis) return 'Der Beginn liegt nach dem Ende.'

  const tage = tageImZeitraum(von, bis)
  const zusatz = ` · ${tage} ${tage === 1 ? 'Tag' : 'Tage'}`
  if (von === bis) return TAG_VOLL.format(tag(von)) + zusatz

  const [jv, mv, dv] = von.split('-')
  const [jb, mb, db] = bis.split('-')
  const nr = (s: string) => String(Number(s))
  if (jv === jb && mv === mb) {
    return `${nr(dv)}. – ${nr(db)}. ${MONAT.format(tag(bis))} ${jb}${zusatz}`
  }
  if (jv === jb) {
    return `${nr(dv)}. ${MONAT.format(tag(von))} – `
         + `${nr(db)}. ${MONAT.format(tag(bis))} ${jb}${zusatz}`
  }
  return `${nr(dv)}. ${MONAT.format(tag(von))} ${jv} – `
       + `${nr(db)}. ${MONAT.format(tag(bis))} ${jb}${zusatz}`
}
